import { useState, useEffect, useContext, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"
import BreadCrumbs from "../components/BreadCrumbs"
import PageHeading from "../components/PageHeading"
import {
    APIProvider,
    Map,
    AdvancedMarker,
    Pin,
    useMap,
    InfoWindow,
} from "@vis.gl/react-google-maps"
import VicinityContext from "../context/vicinityContext"
import { colorCode, safetyPercentage } from "../utilities/helpers"
import CardSummary from "../components/CardSummary"
import PersonBadge from "../components/PersonBadge"
import ChapereeSummary from "../components/ChapereeSummary"
import RiskProfileModal from "../components/RiskProfileModal"
import CommunitySidebar from "../components/CommunitySidebar"
import { useCommunityManager } from "../hooks/useCommunityManager"
import { markerMatchesMapFilter } from "../utilities/communities"
import { usePreferences } from "../hooks/usePreferences"

// Helper component to get map instance
function MapInstanceComponent({ setMapInstance, setMapIsLoaded }) {
    const map = useMap()
    useEffect(() => {
        if (map) {
            setMapInstance(map)
            setMapIsLoaded(true)
        }
    }, [map, setMapInstance, setMapIsLoaded])
    return null
}

const libraries = ["marker"]

// The Community map filter input's dropdown presets.
const FILTER_OPTIONS = [
    { type: "atRisk", label: "At risk", icon: "bi-exclamation-triangle" },
    { type: "watchlist", label: "My watchlist", icon: "bi-pin-angle" },
    { type: "nearby", label: "Nearby", icon: "bi-geo-alt" },
]

export default function Dashboard() {
    const [mapCenter, setMapCenter] = useState({ lat: 34.0522, lng: -118.2437 })
    const [mapZoom, setMapZoom] = useState(2)
    // The map filter, only while the "Filter by..." button is on (toggling
    // clears it): typed text applied on Enter/search click, or a preset
    // picked from the input's dropdown. See markerMatchesMapFilter.
    const [isFilterActive, setIsFilterActive] = useState(false)
    const [filterInput, setFilterInput] = useState("")
    const [mapFilter, setMapFilter] = useState(null)
    const [showFilterOptions, setShowFilterOptions] = useState(false)
    const [userId, setUserId] = useState(null)
    const communityManager = useCommunityManager()
    const { communities } = communityManager
    const [mapIsLoadedState, setMapIsLoadedState] = useState(false)
    const [mapError, setMapError] = useState(null)
    const mapRef = useRef(null)
    const { mapStyle } = usePreferences()

    const { place, spotlightPlaces, location } = useContext(VicinityContext)

    const [selectedMarker, setSelectedMarker] = useState(null)

    // Initialize user ID from localStorage
    useEffect(() => {
        const userData = localStorage.getItem("chaperon_user_data")
        if (userData) {
            const user = JSON.parse(userData)
            setUserId(user.id)
        }
    }, [])

    // Effect for updating map center/zoom based on selected location or initial geolocation
    useEffect(() => {
        if (place && place.latitude != null && place.longitude != null) {
            const newLat = parseFloat(place.latitude)
            const newLng = parseFloat(place.longitude)

            if (!isNaN(newLat) && !isNaN(newLng)) {
                // Only update if the selected-location center is significantly different
                if (
                    Math.abs(newLat - mapCenter.lat) > 0.000001 ||
                    Math.abs(newLng - mapCenter.lng) > 0.000001
                ) {
                    setMapCenter({ lat: newLat, lng: newLng })
                }
                // Optionally, set a specific zoom level when a location is selected
                // Also, resulting effect will lock the map center/zoom
                // if (mapZoom !== 2) {
                //     setMapZoom(2)
                // }
            }
        } else if (
            navigator.geolocation &&
            !mapRef.current?.getCenter() // Attempt geolocation only if map hasn't been centered by selected location data
        ) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    // Check again if selected location data became available in the meantime or if map is still not centered
                    if (
                        (!place ||
                            place.latitude == null ||
                            place.longitude == null) &&
                        mapZoom !== 12
                    ) {
                        setMapCenter({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        })
                        // if (mapZoom !== 12) {
                        //    setMapZoom(12);
                        // }
                    }
                },
                (error) => {
                    console.error("Error getting geolocation:", error)
                },
            )
        }
        // Limit dependencies to selected location state (`place`) to avoid unnecessary updates that would lock the map center/zoom
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [place])

    // Breadcrumbs for navigation
    const crumbs = [{ path: "/community", name: "Community" }]

    // Browser coordinates once a fix has arrived; the initial 0,0 placeholder
    // (no timestamp) means none yet, which disables "Nearby".
    const nearbyOrigin = location?.timestamp ? location.coordinates : null

    const showMarker = (item) =>
        !isFilterActive || markerMatchesMapFilter(item, mapFilter, nearbyOrigin)

    const applyMapFilter = (filter) => {
        setMapFilter(filter)
        if (
            selectedMarker &&
            !markerMatchesMapFilter(selectedMarker, filter, nearbyOrigin)
        ) {
            setSelectedMarker(null)
        }
    }

    const handleFilterSubmit = (e) => {
        e.preventDefault()
        if (!isFilterActive) return
        const preset = FILTER_OPTIONS.find(
            (option) => option.label === filterInput,
        )
        // The box still shows a picked option's label: keep that filter.
        if (preset && mapFilter?.type === preset.type) return
        const term = filterInput.trim()
        applyMapFilter(term ? { type: "text", term } : null)
        setShowFilterOptions(false)
    }

    const pickFilterOption = (option) => {
        setFilterInput(option.label)
        applyMapFilter({ type: option.type })
        setShowFilterOptions(false)
    }

    const toggleFilter = () => {
        setIsFilterActive((active) => !active)
        setFilterInput("")
        setMapFilter(null)
        setShowFilterOptions(false)
    }

    const handleCameraChange = (ev) => {
        const newCenter = ev.detail.center
        const newZoom = ev.detail.zoom
        if (
            Math.abs(newCenter.lat - mapCenter.lat) > 0.000001 ||
            Math.abs(newCenter.lng - mapCenter.lng) > 0.000001
        ) {
            setMapCenter(newCenter)
        }
        if (newZoom !== mapZoom) {
            setMapZoom(newZoom)
        }
    }

    const handleMarkerClick = (markerData) => {
        setSelectedMarker(markerData)
    }

    const handleInfoWindowClose = () => {
        setSelectedMarker(null)
    }

    return (
        <div>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Chaperon - Community</title>
            </Helmet>

            <BreadCrumbs crumbs={crumbs} />

            <RiskProfileModal />
            <div className="dashboard-container">
                <PageHeading title="Community">
                    <form className="d-flex" onSubmit={handleFilterSubmit}>
                        <Link
                            to="/community/checker"
                            className="btn btn-outline-secondary w-25 me-2"
                        >
                            <i className="bi bi-heart-fill me-1"></i>{" "}
                            Checker
                        </Link>

                        <Link
                            to="/community/convoy"
                            className="btn btn-outline-secondary w-25 me-2"
                        >
                            <i className="bi bi-eye-fill me-1"></i>{" "}
                            Convoy
                        </Link>

                        <input
                            type="checkbox"
                            className="btn-check"
                            id="btn-check-filter"
                            autoComplete="off"
                            checked={isFilterActive}
                            onChange={toggleFilter}
                        />
                        <label
                            className="btn btn-outline-secondary w-25 me-2"
                            htmlFor="btn-check-filter"
                        >
                            <i className="bi bi-funnel me-1"></i> Filter by...
                        </label>

                        <div className="position-relative w-50 me-2">
                            <input
                                id="filter-map-chaperees"
                                name="filter-map-chaperees"
                                className="form-control"
                                type="search"
                                autoComplete="off"
                                placeholder={
                                    isFilterActive
                                        ? "Type a name, or pick a filter..."
                                        : "Turn on Filter by... to filter the map"
                                }
                                disabled={!isFilterActive}
                                value={filterInput}
                                onChange={(e) => setFilterInput(e.target.value)}
                                onFocus={() => setShowFilterOptions(true)}
                                // Picking an option keeps focus here, so a later click
                                // fires no focus event; reopen on click too.
                                onClick={() => setShowFilterOptions(true)}
                                onBlur={() => setShowFilterOptions(false)}
                                aria-controls="filter-map-options"
                                aria-expanded={showFilterOptions}
                            />
                            {isFilterActive && showFilterOptions && (
                                <ul
                                    id="filter-map-options"
                                    className="dropdown-menu show w-100"
                                >
                                    {FILTER_OPTIONS.map((option) => {
                                        const unavailable =
                                            option.type === "nearby" &&
                                            !nearbyOrigin
                                        return (
                                            <li key={option.type}>
                                                <button
                                                    type="button"
                                                    className={`dropdown-item${
                                                        mapFilter?.type === option.type
                                                            ? " active"
                                                            : ""
                                                    }`}
                                                    disabled={unavailable}
                                                    title={
                                                        unavailable
                                                            ? "Your current location is unavailable"
                                                            : undefined
                                                    }
                                                    // mousedown, before the input's blur closes the list
                                                    onMouseDown={(e) => {
                                                        e.preventDefault()
                                                        if (!unavailable) pickFilterOption(option)
                                                    }}
                                                >
                                                    <i className={`bi ${option.icon} me-2`}></i>
                                                    {option.label}
                                                    {option.type === "nearby" &&
                                                        (unavailable
                                                            ? " (location unavailable)"
                                                            : " (within 10 km)")}
                                                </button>
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}
                        </div>
                        <button
                            className="btn btn-danger"
                            type="submit"
                            disabled={!isFilterActive}
                        >
                            <i className="bi bi-search"></i>
                        </button>
                    </form>
                </PageHeading>

                <div className="dashboard-content">
                    <div className="row">
                        {/* Main Content - Full Width */}
                        <div className="col-12">
                            <div className="card map-container">
                                <div className="card-body p-0">
                                    <APIProvider
                                        apiKey={
                                            import.meta.env
                                                .VITE_GOOGLE_MAPS_JS_API_KEY
                                        }
                                        libraries={libraries}
                                        onError={(e) => {
                                            console.error(
                                                "APIProvider Error:",
                                                e,
                                            )
                                            setMapError(
                                                "Failed to load Google Maps API.",
                                            )
                                        }}
                                    >
                                        <Map
                                            style={{
                                                width: "100%",
                                                height: "70vh",
                                                borderRadius: "0.375rem",
                                            }}
                                            center={mapCenter}
                                            zoom={mapZoom}
                                            minZoom={mapZoom}
                                            maxZoom={mapZoom}
                                            mapId={"c7537b8b22c3b62bd65540a0"}
                                            mapTypeId={mapStyle}
                                            gestureHandling={"greedy"}
                                            disableDefaultUI={false}
                                            zoomControl={false}
                                            scrollwheel={false}
                                            disableDoubleClickZoom={true}
                                            onCameraChanged={handleCameraChange}
                                            // The info window has no header (so no X);
                                            // clicking the map closes it instead.
                                            onClick={handleInfoWindowClose}
                                        >
                                            <MapInstanceComponent
                                                setMapInstance={(map) =>
                                                    (mapRef.current = map)
                                                }
                                                setMapIsLoaded={
                                                    setMapIsLoadedState
                                                }
                                            />

                                            {place &&
                                                showMarker(place) &&
                                                place.latitude != null &&
                                                place.longitude != null && (
                                                    <AdvancedMarker
                                                        position={{
                                                            lat: parseFloat(
                                                                place.latitude,
                                                            ),
                                                            lng: parseFloat(
                                                                place.longitude,
                                                            ),
                                                        }}
                                                        title={
                                                            place?.fromGoogle
                                                                ?.name ||
                                                            "Current Location"
                                                        }
                                                        onClick={() =>
                                                            handleMarkerClick(
                                                                place,
                                                            )
                                                        } // Set selected place marker data
                                                    >
                                                        <Pin
                                                            background={colorCode(
                                                                safetyPercentage(
                                                                    place.overall_safety,
                                                                ),
                                                                "hex",
                                                            )}
                                                            borderColor={
                                                                "#000000"
                                                            }
                                                            glyphColor={
                                                                "#FFFFFF"
                                                            }
                                                        />
                                                    </AdvancedMarker>
                                                )}

                                            {spotlightPlaces?.map(
                                                (spot, index) => {
                                                    if (
                                                        showMarker(spot) &&
                                                        spot.latitude != null &&
                                                        spot.longitude != null
                                                    ) {
                                                        const safetyScore =
                                                            safetyPercentage(
                                                                spot.overall_safety,
                                                            )
                                                        const markerColor =
                                                            colorCode(
                                                                safetyScore,
                                                                "hex",
                                                            )
                                                        return (
                                                            <AdvancedMarker
                                                                key={`spot-${index}`}
                                                                position={{
                                                                    lat: parseFloat(
                                                                        spot.latitude,
                                                                    ),
                                                                    lng: parseFloat(
                                                                        spot.longitude,
                                                                    ),
                                                                }}
                                                                title={
                                                                    spot.name ||
                                                                    `Location ${index}`
                                                                }
                                                                onClick={() =>
                                                                    handleMarkerClick(
                                                                        spot,
                                                                    )
                                                                } // Set selected Spotlight place marker data
                                                            >
                                                                <Pin
                                                                    background={
                                                                        markerColor
                                                                    }
                                                                    borderColor={
                                                                        "#505050"
                                                                    }
                                                                    glyphColor={
                                                                        "#FFFFFF"
                                                                    }
                                                                />
                                                            </AdvancedMarker>
                                                        )
                                                    }
                                                    return null
                                                },
                                            )}

                                            {/* Render community persons as markers */}
                                            {communities.flatMap(
                                                (community) =>
                                                    community.persons?.map(
                                                        (person) => {
                                                            if (
                                                                showMarker(person) &&
                                                                person.latitude !=
                                                                    null &&
                                                                person.longitude !=
                                                                    null
                                                            ) {
                                                                return (
                                                                    <AdvancedMarker
                                                                        key={`community-${community.id}-person-${person.id}`}
                                                                        position={{
                                                                            lat: parseFloat(
                                                                                person.latitude,
                                                                            ),
                                                                            lng: parseFloat(
                                                                                person.longitude,
                                                                            ),
                                                                        }}
                                                                        title={
                                                                            person.username ||
                                                                            "Community Member"
                                                                        }
                                                                        onClick={() =>
                                                                            handleMarkerClick(
                                                                                {
                                                                                    ...person,
                                                                                    chapereeType:
                                                                                        "person",
                                                                                },
                                                                            )
                                                                        }
                                                                    >
                                                                        {person.profile_picture_url ? (
                                                                            <PersonBadge
                                                                                person={
                                                                                    person
                                                                                }
                                                                                mode={
                                                                                    "mini"
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <i
                                                                                className={`bi bi-person-circle text-${colorCode(
                                                                                    safetyPercentage(
                                                                                        person.overall_safety,
                                                                                    ),
                                                                                )} border border-dark rounded-circle p-1`}
                                                                                style={{
                                                                                    fontSize:
                                                                                        "24px",
                                                                                    color: "#000",
                                                                                    backgroundColor:
                                                                                        "#fff",
                                                                                }}
                                                                            ></i>
                                                                        )}
                                                                    </AdvancedMarker>
                                                                )
                                                            }
                                                            return null
                                                        },
                                                    ) || [],
                                            )}

                                            {/* Render community places as markers */}
                                            {communities.flatMap((community) =>
                                                (community.places || []).map(
                                                    (place) => {
                                                        if (
                                                            showMarker(place) &&
                                                            place.latitude !=
                                                                null &&
                                                            place.longitude !=
                                                                null
                                                        ) {
                                                            const safetyScore =
                                                                safetyPercentage(
                                                                    place.overall_safety,
                                                                )
                                                            const markerColor =
                                                                colorCode(
                                                                    safetyScore,
                                                                    "hex",
                                                                )
                                                            return (
                                                                <AdvancedMarker
                                                                    key={`community-${community.id}-place-${place.id}`}
                                                                    position={{
                                                                        lat: parseFloat(
                                                                            place.latitude,
                                                                        ),
                                                                        lng: parseFloat(
                                                                            place.longitude,
                                                                        ),
                                                                    }}
                                                                    title={
                                                                        place.name ||
                                                                        "Community Place"
                                                                    }
                                                                    onClick={() =>
                                                                        handleMarkerClick(
                                                                            place,
                                                                        )
                                                                    }
                                                                >
                                                                    <Pin
                                                                        background={
                                                                            markerColor
                                                                        }
                                                                        borderColor={
                                                                            "#000000"
                                                                        }
                                                                        glyphColor={
                                                                            "#FFFFFF"
                                                                        }
                                                                    />
                                                                </AdvancedMarker>
                                                            )
                                                        }
                                                        return null
                                                    },
                                                ),
                                            )}

                                            {/* Conditionally render InfoWindow */}
                                            {selectedMarker && (
                                                <InfoWindow
                                                    position={{
                                                        lat: parseFloat(
                                                            selectedMarker.latitude,
                                                        ),
                                                        lng: parseFloat(
                                                            selectedMarker.longitude,
                                                        ),
                                                    }}
                                                    onCloseClick={
                                                        handleInfoWindowClose
                                                    }
                                                    headerDisabled
                                                    // You might need to adjust pixelOffset if the InfoWindow doesn't align perfectly with the pin
                                                    // pixelOffset={new window.google.maps.Size(0, -30)} // Example offset
                                                >
                                                    {selectedMarker.chapereeType ===
                                                    "person" ? (
                                                        <ChapereeSummary
                                                            person={
                                                                selectedMarker
                                                            }
                                                            mode="mini"
                                                        />
                                                    ) : (
                                                        <CardSummary
                                                            place={
                                                                selectedMarker
                                                            }
                                                            mode="mini"
                                                        />
                                                    )}
                                                </InfoWindow>
                                            )}
                                        </Map>
                                        {!mapIsLoadedState && !mapError && (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    height: "70vh",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "0.375rem",
                                                    position: "absolute", // Overlay on top of map container
                                                    top: 0,
                                                    left: 0,
                                                }}
                                            >
                                                <div className="text-center">
                                                    <i
                                                        className="bi bi-map-fill"
                                                        style={{
                                                            fontSize: "3rem",
                                                        }}
                                                    ></i>
                                                    <h3>Loading map...</h3>
                                                    <div
                                                        className="spinner-border text-primary"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Loading...
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {mapError && (
                                            <div
                                                className="alert alert-danger m-3"
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    zIndex: 10,
                                                }}
                                            >
                                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                {mapError}
                                            </div>
                                        )}
                                    </APIProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CommunitySidebar manager={communityManager} />
        </div>
    )
}
